import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Fragment } from 'react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { Disclosure, Popover, Transition } from '@headlessui/react';
import classNames from '../../../libs/utils/classNames';
import { useSearchParams } from 'react-router-dom';
import PrimaryButtonFull from '../../Buttons/PrimaryButtonFull';
import { useWindowSize } from '../../../libs/utils/helpers';
import QueryString from 'qs';

interface Props {
  attributeName: string;
  label?: string;
  quickFilters?: 'default' | 'treasury';
}

const formatDate = (d: Date): string => d.toISOString().split('T')[0];

const TREASURY_QUICK_FILTERS = [
  {
    label: 'Ultima settimana',
    getRange: () => {
      const to = formatDate(new Date());
      const d = new Date();
      d.setDate(d.getDate() - 7);
      return { from: formatDate(d), to };
    },
  },
  {
    label: 'Ultimo mese',
    getRange: () => {
      const to = formatDate(new Date());
      const d = new Date();
      d.setMonth(d.getMonth() - 1);
      return { from: formatDate(d), to };
    },
  },
];

const DEFAULT_QUICK_FILTERS: typeof TREASURY_QUICK_FILTERS = [];

const parseDateFilter = (value: string | null) => {
  if (!value) return {};

  const parsedFilter = QueryString.parse(value);
  const filterLabel = Object.entries(parsedFilter).map(([, item]) => item as any)[0]
    ?.label;

  if (!filterLabel) return {};

  const parsedDate = QueryString.parse(filterLabel);
  return {
    from: parsedDate.from ? String(parsedDate.from) : undefined,
    to: parsedDate.to ? String(parsedDate.to) : undefined,
  };
};

const DateFilter: React.FC<Props> = ({ attributeName, label, quickFilters = 'default' }) => {
  const QUICK_FILTERS = quickFilters === 'treasury' ? TREASURY_QUICK_FILTERS : DEFAULT_QUICK_FILTERS;
  const [searchParams, setSearchParams] = useSearchParams();
  const [windowWidth] = useWindowSize();
  const fromInputRef = useRef<HTMLInputElement | null>(null);
  const toInputRef = useRef<HTMLInputElement | null>(null);
  const [date, setDate] = useState<{
    from?: string | undefined;
    to?: string | undefined;
  }>({});

  const syncInputs = useCallback((from?: string, to?: string) => {
    if (fromInputRef.current) fromInputRef.current.value = from || '';
    if (toInputRef.current) toInputRef.current.value = to || '';
  }, []);

  const applyFilter = (from?: string, to?: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete('page');

    if (from || to) {
      nextSearchParams.set(
        attributeName,
        QueryString.stringify([
          {
            label: QueryString.stringify({ from, to }),
            value: attributeName,
            type: 'DATE',
          },
        ]),
      );
    } else {
      nextSearchParams.delete(attributeName);
    }

    setSearchParams(nextSearchParams);
  };

  const onSearchDate = () => {
    const from = fromInputRef.current?.value || undefined;
    const to = toInputRef.current?.value || undefined;

    setDate({ from, to });
    applyFilter(from, to);
  };

  const onQuickFilter = (from?: string, to?: string) => {
    setDate({ from, to });
    syncInputs(from, to);
    applyFilter(from, to);
  };

  useEffect(() => {
    const rawDateFilter = searchParams.get(attributeName);

    if (rawDateFilter === '') {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(attributeName);
      setSearchParams(nextSearchParams);
      setDate({});
      syncInputs();
      return;
    }

    const parsedDate = parseDateFilter(rawDateFilter);

    setDate(parsedDate);
    syncInputs(parsedDate.from, parsedDate.to);
  }, [attributeName, searchParams, setSearchParams, syncInputs]);

  const DateInputs = () => (
    <>
      <div className='w-full'>
        <label htmlFor='data-from' className='text-sm text-gray-500 flex justify-between'>
          <div>Data da </div>
        </label>
        <div className='mt-1 flex items-center'>
          <input
            id='data-from'
            ref={fromInputRef}
            defaultValue={date.from || ''}
            type='date'
            name='data-from'
            className='appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-gray-500 text-gray-800 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'
          />
        </div>
      </div>

      <div className='w-full pb-4'>
        <label htmlFor='data-to' className='text-sm text-gray-500 flex justify-between'>
          <div>Data a </div>
        </label>
        <div className='mt-1 flex items-center'>
          <input
            id='data-to'
            ref={toInputRef}
            defaultValue={date.to || ''}
            type='date'
            name='data-to'
            className='appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-0 focus:border-gray-500 text-gray-800 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'
          />
        </div>
      </div>

      <PrimaryButtonFull children={'Cerca per data'} onClick={onSearchDate} />
    </>
  );

  const QuickChips = () => (
    <>
      {QUICK_FILTERS.map((chip) => {
        const range = chip.getRange();
        const isActive = date.from === range.from && date.to === range.to;
        return (
          <button
            key={chip.label}
            type='button'
            onClick={() => onQuickFilter(range.from, range.to)}
            className={classNames(
              'text-xs px-2.5 py-1 rounded-full border transition-colors whitespace-nowrap',
              isActive
                ? 'bg-primary text-white border-primary'
                : 'border-gray-300 text-gray-600 hover:border-primary hover:text-primary',
            )}
          >
            {chip.label}
          </button>
        );
      })}
    </>
  );

  return windowWidth < 1280 ? (
    <Disclosure
      as='div'
      key='select-date'
      className='border-t border-gray-200 px-4 py-6 pr-1'
    >
      {({ open }) => (
        <>
          <h3 className='-mx-2 -my-3 flow-root'>
            <Disclosure.Button className='px-2 py-3 bg-white w-full flex items-center justify-between text-sm text-gray-400'>
              <span className='font-normal text-gray-800'>
                {label ?? 'Data'}
              </span>
              <span className='ml-6 flex items-center'>
                <ChevronDownIcon
                  className={classNames(
                    open ? '-rotate-180' : 'rotate-0',
                    'h-5 w-5 transform',
                  )}
                  aria-hidden='true'
                />
              </span>
            </Disclosure.Button>
          </h3>
          <Disclosure.Panel className='pt-6'>
            <div className='w-full space-y-6 pr-3'>
              <div className='flex flex-wrap gap-1'>
                <QuickChips />
              </div>
              <DateInputs />
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  ) : (
    <div className='px-4 flex items-center gap-2'>
      <Popover key='sections' className='relative inline-block text-left'>
        <Popover.Button className='group inline-flex justify-center text-sm font-normal text-gray-700 hover:text-gray-800'>
          <span>{label ?? 'Data'}</span>
          <ChevronDownIcon
            className='flex-shrink-0 -mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500'
            aria-hidden='true'
          />
        </Popover.Button>

        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Popover.Panel className='origin-top-right absolute left-0 mt-2 bg-white rounded-md shadow-2xl p-4 ring-1 ring-black ring-opacity-5 focus:outline-none'>
            <div className='w-56 space-y-6'>
              {QUICK_FILTERS.length > 0 && (
                <div className='flex flex-wrap gap-1'>
                  <QuickChips />
                </div>
              )}
              <DateInputs />
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    </div>
  );
};
export default DateFilter;
